import React, { Component, MouseEvent } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Typography } from "@material-ui/core";
import { login } from "services/auth";
import { AuthenticationError } from "util/errors";
import Form, { FormStatus } from "components/forms/Form";
import TextField from "components/forms/TextField";
import ProgressSpinner from "components/ProgressSpinner";
import { ValidationGroup, Validators } from "util/validation";
import GoogleButton from "components/GoogleButton";
import RouteURLs from "RouteURLs";
import { Link } from "react-router-dom";
import bg from "../../../assets/img/steps/geo-2.svg";
import "./index.scss";

interface LoginFormProps extends RouteComponentProps {
  onRegister?: () => void;
  onLoggedIn: () => void;
}

interface LoginFormState {
  status: FormStatus;
  error?: string;
}

interface LoginRequest {
  email: string;
  password: string;
}

class LoginForm extends Component<LoginFormProps, LoginFormState> {
  private readonly validation: ValidationGroup;
  private readonly model: LoginRequest;

  state = {
    status: FormStatus.None,
    error: undefined,
  };

  constructor(props: any) {
    super(props);
    this.model = {
      email: "",
      password: "",
    };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("email", {
      required: Validators.required,
    });
    this.validation.addField("password", {
      required: Validators.required,
    });
  }

  onRegisterClick = (ev: MouseEvent) => {
    ev.preventDefault();
    if (this.props.onRegister) {
      this.props.onRegister();
    }
  };

  onLoginSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    login(this.model.email, this.model.password).then(
      () => {
        this.setState({ status: FormStatus.Success });
        this.props.onLoggedIn();
      },
      (err) => {
        if (err instanceof AuthenticationError) {
          this.setState({
            status: FormStatus.Failure,
            error: "Credentials are invalid.",
          });
        } else {
          this.setState({
            status: FormStatus.Failure,
            error: "Unknown error.",
          });
        }
      }
    );
  };

  onGoogleButtonLoggedIn = () => {
    this.setState({ status: FormStatus.Success });
    this.props.onLoggedIn();
  };

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  render() {
    const disabled =
      this.state.status === FormStatus.Submitting ||
      this.state.status === FormStatus.Success;
    return (
      <Form
        className="login-form"
        onSubmit={this.onLoginSubmit}
        validationGroup={this.validation}
      >
        <div className="background-image">
          <img src={bg} alt="box illustration" />
        </div>
        <Typography variant="h1" component="h2" className="section-title">
          Log in
        </Typography>
        <Typography variant="body1" component="h1" className="text">
          Find, brand and book your space in 5 minutes.
        </Typography>
        <TextField
          name="email"
          label="Email"
          type="text"
          required={true}
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{ required: "Email is required" }}
          disabled={disabled}
          onInput={this.onFieldInput}
          variant="outlined"
        />
        <TextField
          name="password"
          label="Password"
          type="password"
          className="password"
          required={true}
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{ required: "Password is required" }}
          disabled={disabled}
          onInput={this.onFieldInput}
          variant="outlined"
        />
        <p className="reset-password">
          <Link to={RouteURLs.REQUEST_PASSWORD_RESET}>
            Forgot your password?
          </Link>
        </p>
        <section className="actions">
          <button className="submit-button" type="submit" disabled={disabled}>
            {this.state.status === FormStatus.Submitting && <ProgressSpinner />}
            Log In
          </button>
          <p className="register">
            Don't have an account yet?{" "}
            <button onClick={this.onRegisterClick}>Sign up</button>
          </p>
        </section>
        {this.state.status === FormStatus.Failure && (
          <p className="error">{this.state.error}</p>
        )}

        <div className="social-login-container">
          <GoogleButton onLoggedIn={this.onGoogleButtonLoggedIn} />
        </div>
      </Form>
    );
  }
}

export default withRouter(LoginForm);
